// The Price map (AdminPrices.dc.html): every Stripe price, and what it opens when someone pays, with
// the Mismatches and the 7 rules beside them. Owner and admin open it (the shell); only an owner
// maps a price or edits a row (row level security: price_map's insert and update are owner only),
// and an admin reads it as it is. Mismatch cards are owner and admin.
//
// Reads: hub.price_map, price_map_products and price_map_balances (owner and admin), the catalogue,
// hub.today_jobs() for the 2 mismatch kinds, and stripe-prices' owner and admin part for the amounts
// and the Stripe prices not on the map. No amount is stored anywhere: each is read from Stripe here.
import { useCallback, useMemo, useState } from 'react'
import { PageHeader, Table, Dot, Problem, Loading, EmptyState, InfoNote, useAdmin, useLoad } from '../ui/index.js'
import { productDot } from '../ui/format.js'
import { WORDS, RULES, MISMATCH, MISMATCH_KINDS, buildRows, chunks, unmappedPricesOf } from '../prices/model.js'
import { EditDialog, MapDialog, PRICE_COLUMNS } from '../prices/forms.jsx'
import { PaidCard, NoPaymentCard, UnmappedCard } from '../prices/Mismatches.jsx'
import '../../styles/admin-prices.css'

const CATALOGUE_COLUMNS = 'id, slug, path, name, type, access_kind, state, look, moved_in, terms_line, sort_order, settings'

// The dot beside a row: the product's own, and the ink for a price that opens nothing the hub knows
// (the board's Strategy Session) and for a private client space (the board's 1-2-1 row)
function dotFor(row) {
  if (!row.dotProduct) return 'var(--shell-ink)'
  if (row.dotProduct.type === 'client_space') return 'var(--shell-ink)'
  return productDot(row.dotProduct)
}

function StatusChip({ status, children }) {
  return <span className={`pm-chip pm-chip--${status}`}>{children}</span>
}

export default function Prices({ role }) {
  const { api, setFlash } = useAdmin()
  const owner = role === 'owner'
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick(t => t + 1), [])
  const [editing, setEditing] = useState(null)
  const [mapping, setMapping] = useState(null) // { price, job? }
  const [doneLocal, setDoneLocal] = useState({})
  const setDone = key => text => setDoneLocal(d => ({ ...d, [key]: text }))

  const hub = useLoad(async () => {
    const [prices, links, balances, catalogue] = await Promise.all([
      api.run(api.from('price_map').select(PRICE_COLUMNS).order('stripe_price_id')),
      api.run(api.from('price_map_products').select('stripe_price_id, product_id, cohort_id')),
      api.run(api.from('price_map_balances').select('place_price_id, balance_price_id, pay_button')),
      api.run(api.from('catalogue').select(CATALOGUE_COLUMNS).order('sort_order')),
    ])
    return { prices, links, balances, catalogue }
  }, [tick])

  // the board's order: payments that opened nothing first, then access with no payment; open before done
  const jobsLoad = useLoad(async () => (await api.today_jobs())
    .filter(j => MISMATCH_KINDS.includes(j.kind))
    .sort((a, b) => MISMATCH_KINDS.indexOf(a.kind) - MISMATCH_KINDS.indexOf(b.kind) || (a.status === 'open' ? 0 : 1) - (b.status === 'open' ? 0 : 1)), [tick])
  const jobs = jobsLoad.data || []

  // what Stripe says each price costs, and what each held checkout paid: up to 50 ids a call, the
  // prices asked once the map is read and the checkouts once the jobs are
  const priceIds = (hub.data?.prices || []).map(p => p.stripe_price_id)
  const checkoutIds = [...new Set(jobs.map(j => j.detail?.checkout_session_id).filter(id => /^cs_/.test(id || '')))]
  const priceAmounts = useLoad(async () => {
    if (!hub.data) return null
    const out = {}
    const answers = await Promise.all(chunks(priceIds).map(ids => api.stripe_prices({ action: 'amounts', price_ids: ids })))
    for (const a of answers) Object.assign(out, a.prices || {})
    return out
  }, [priceIds.join(','), Boolean(hub.data)])
  const checkoutAmounts = useLoad(async () => {
    if (!checkoutIds.length) return {}
    const out = {}
    const answers = await Promise.all(chunks(checkoutIds).map(ids => api.stripe_prices({ action: 'amounts', checkout_session_ids: ids })))
    for (const a of answers) Object.assign(out, a.checkouts || {})
    return out
  }, [checkoutIds.join(',')])
  const amountsData = useMemo(() => (priceAmounts.data ? { prices: priceAmounts.data, checkouts: checkoutAmounts.data || {} } : null),
    [priceAmounts.data, checkoutAmounts.data])
  const amounts = {
    status: priceAmounts.status === 'error' || checkoutAmounts.status === 'error' ? 'error' : priceAmounts.status,
    data: amountsData,
    reload: () => { priceAmounts.reload(); checkoutAmounts.reload() },
  }

  const unmapped = useLoad(async () => (await api.stripe_prices({ action: 'unmapped' })).prices || [], [tick])

  const rows = useMemo(() => (hub.data ? buildRows({ ...hub.data, amounts: priceAmounts.status === 'ready' && amountsData ? amountsData : null }) : []),
    [hub.data, priceAmounts.status, amountsData])
  const mappedIds = useMemo(() => new Set((hub.data?.prices || []).filter(p => p.status === 'mapped').map(p => p.stripe_price_id)), [hub.data])

  // a price a payment card already names isn't listed again on its own
  const named = new Set(jobs.filter(j => j.status === 'open').flatMap(unmappedPricesOf))
  const [seenUnmapped, setSeenUnmapped] = useState({})
  const unmappedList = useMemo(() => {
    const now = (unmapped.data || []).filter(p => !named.has(p.id))
    const ids = new Set(now.map(p => p.id))
    // a price just mapped here stays on the list with its done line until the page is opened again
    return [...now, ...Object.values(seenUnmapped).filter(p => !ids.has(p.id) && doneLocal[`unmapped:${p.id}`])]
  }, [unmapped.data, named.size, seenUnmapped, doneLocal]) // eslint-disable-line react-hooks/exhaustive-deps

  function openMap(price, job = null) {
    setSeenUnmapped(s => ({ ...s, [price.id]: price }))
    setMapping({ price, job })
  }

  // a payment card's Map this price: the price as Stripe gave it in the not-on-the-map list
  function mapFromJob(job) {
    const id = unmappedPricesOf(job)[0]
    const found = (unmapped.data || []).find(p => p.id === id)
    if (!found) {
      setFlash({ tone: 'problem', text: unmapped.status === 'error'
        ? "Stripe couldn't be read just now, so the price can't be mapped. Try again in a minute."
        : "That price isn't in Stripe's list of prices off the map, so it can't be mapped here." })
      return
    }
    openMap(found, job)
  }

  async function mapped(result) {
    const { price, job } = mapping
    setMapping(null)
    if (job) {
      // the held payment, applied now its price is on the map (resolve-payment)
      try {
        const answer = await api.resolve_payment({ flag_id: job.flag_ids[0] })
        setDoneLocal(d => ({ ...d, [job.job_key]: `Mapped to ${result.opens}. ${String(answer.message || '').replace(/^Done\.\s*/, '')}`.trim() }))
      } catch (e) {
        setFlash({ tone: 'problem', text: `Mapped to ${result.opens}, but the payment wasn't applied: ${api.words(e)}` })
      }
    } else {
      setDoneLocal(d => ({ ...d, [`unmapped:${price.id}`]: `Mapped. A payment on it now opens ${result.opens}.` }))
    }
    reload()
  }

  const columns = [
    {
      key: 'price', label: 'Stripe price', width: 'minmax(0, 1.3fr)', primary: true,
      render: r => (
        <span className="pm-price">
          <Dot colour={dotFor(r)} />
          <span className="pm-price-text">
            <span className={`pm-price-amount${r.priceKnown || r.source !== 'price' ? '' : ' pm-price-amount--quiet'}`}>
              {r.price || (priceAmounts.status === 'error' ? WORDS.notRead : 'Reading Stripe')}
            </span>
            <span className="pm-price-label">{r.label}</span>
            {r.source === 'price' && !r.active && <span className="pm-price-label">{WORDS.archived}</span>}
          </span>
        </span>
      ),
    },
    { key: 'from', label: 'Sold from', width: 'minmax(0, 1fr)', render: r => <span className={`pm-from${r.from ? '' : ' pm-quiet'}`}>{r.from || WORDS.notSet}</span> },
    { key: 'opens', label: 'Opens', width: 'minmax(0, 1.1fr)', render: r => <span className="pm-opens">{r.opens}</span> },
    {
      key: 'rule', label: 'Access', width: 'minmax(0, 1.3fr)',
      render: r => (
        <span className="pm-rule">
          <span>{r.rule}</span>
          {r.place && <span className="pm-rule-line">{r.place.due}</span>}
          {r.place?.passed && <span className="pm-rule-line pm-rule-warn">That date has passed, so a place secured now is past due at once.</span>}
          {r.place && (
            <span className="pm-rule-line">
              {r.place.buttonId
                // "the <amount> balance", or "the balance" when Stripe's amount isn't read (Adam, 5 Oct, question 17)
                ? `The pay button pays the ${r.place.buttonAmount ? `${r.place.buttonAmount} balance` : 'balance'}${r.place.buttonLink ? '' : ', with no payment link yet'}`
                : 'No pay button set yet'}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'status', label: 'Status', width: '120px',
      render: r => (
        <span className="pm-status">
          <StatusChip status={r.status}>{r.statusWord}</StatusChip>
          {owner && r.source === 'price' && (
            <button type="button" className="pm-edit" onClick={() => setEditing(r)} aria-label={`Edit ${r.label}`}>Edit</button>
          )}
        </span>
      ),
    },
  ]

  const openJobs = jobs
  const anyIssue = openJobs.length > 0 || unmappedList.length > 0

  return (
    <>
      <PageHeader eyebrow={WORDS.eyebrow} title={WORDS.title} lede={WORDS.lede} />
      {amounts.status === 'error' && (
        <Problem onRetry={amounts.reload}>Stripe couldn't be read just now, so the amounts aren't shown. Try again in a minute.</Problem>
      )}

      <section className="pm-map" aria-label="Every Stripe price">
        {hub.status === 'loading' && <Loading />}
        {hub.status === 'error' && <Problem error={hub.error} onRetry={hub.reload} />}
        {hub.data && (
          <Table label="Every Stripe price, and what it opens" columns={columns} rows={rows} rowKey={r => r.key} flush className="pm-table"
            empty="Nothing is on the price map yet." />
        )}
        <div className="pm-foot">{WORDS.footer}</div>
      </section>

      <div className="pm-grid">
        <section className="pm-panel" aria-labelledby="pm-mismatches">
          <div className="pm-panel-head">
            <h2 className="pm-panel-title" id="pm-mismatches">{MISMATCH.heading}</h2>
            <span className="pm-panel-aside">{MISMATCH.aside}</span>
          </div>
          {jobsLoad.status === 'loading' && <Loading />}
          {jobsLoad.status === 'error' && <Problem error={jobsLoad.error} onRetry={jobsLoad.reload} />}
          {jobs.map(job => (job.kind === 'paid_no_access'
            ? (
              <PaidCard key={job.job_key} job={job} role={role} mappedIds={mappedIds}
                amount={amounts.data?.checkouts?.[job.detail?.checkout_session_id]?.display || null}
                onMap={mapFromJob} onChanged={reload} doneLocal={doneLocal[job.job_key]} setDone={setDone(job.job_key)} />
            )
            : <NoPaymentCard key={job.job_key} job={job} onChanged={reload} doneLocal={doneLocal[job.job_key]} setDone={setDone(job.job_key)} />))}
          {unmappedList.map(p => (
            <UnmappedCard key={p.id} price={p} role={role} onMap={price => openMap(price)} onChanged={reload}
              doneLocal={doneLocal[`unmapped:${p.id}`]} setDone={text => { setSeenUnmapped(s => ({ ...s, [p.id]: p })); setDone(`unmapped:${p.id}`)(text) }} />
          ))}
          {unmapped.status === 'error' && (
            <InfoNote>Stripe couldn't be read just now, so prices that aren't on the map can't be listed. Try again in a minute.</InfoNote>
          )}
          {jobsLoad.status === 'ready' && unmapped.status !== 'loading' && !anyIssue && <EmptyState className="pm-none">{MISMATCH.none}</EmptyState>}
        </section>

        <section className="pm-panel" aria-labelledby="pm-rules">
          <h2 className="pm-panel-title" id="pm-rules">How payments change access</h2>
          <dl className="pm-rules">
            {RULES.map(u => (
              <div className="pm-rules-row" key={u.when}>
                <dt>{u.when}</dt>
                <dd>{u.then}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      {editing && owner && (
        <EditDialog row={editing} amounts={amounts.data} onClose={() => setEditing(null)}
          onSaved={text => { setEditing(null); setFlash({ text }); reload() }} />
      )}
      {mapping && owner && hub.data && (
        <MapDialog price={mapping.price} catalogue={hub.data.catalogue} prices={hub.data.prices}
          onClose={() => setMapping(null)} onMapped={mapped} />
      )}
    </>
  )
}
